import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { ReactNode } from "react";

interface ConfirmDeleteButtonProps {
  onConfirm: () => void;
  entityName: string;
  entityLabel?: string;
  disabled?: boolean;
  isPending?: boolean;
  /** Optional custom body copy shown in place of the default confirmation text */
  description?: ReactNode;
  /** Fired when the confirmation dialog opens/closes (useful for preflight fetches) */
  onOpenChange?: (open: boolean) => void;
  /** Optional custom dialog title, e.g. "Archive location?" */
  title?: string;
  /** Optional custom label for the confirm action button */
  confirmLabel?: string;
}

export function ConfirmDeleteButton({
  onConfirm,
  entityName,
  entityLabel,
  disabled,
  isPending,
  description,
  onOpenChange,
  title,
  confirmLabel,
}: ConfirmDeleteButtonProps) {
  return (
    <AlertDialog onOpenChange={onOpenChange}>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="icon" disabled={disabled || isPending}>
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title ?? `Delete ${entityName}?`}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-muted-foreground">
              {description ?? (entityLabel
              ? `Are you sure you want to delete "${entityLabel}"? This action cannot be undone.`
                : `Are you sure you want to delete this ${entityName}? This action cannot be undone.`)}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {confirmLabel ?? "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
